/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useRequestCommunityBroadcast } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  colors,
  constantSemanticColors,
  dropShadow,
  IconButton,
  ModalDropdownMenu,
  ModalDropdownMenuItem,
  MoreIcon,
  spacing,
  TrashIcon,
  useAlert,
  useConfirm,
} from "@packages/ui";

const dropdownMenuItemStyles: Interpolation = {
  columnGap: spacing.s,
  minWidth: "180px",
  padding: `${spacing.xs} ${spacing.s}`,
};

const dropdownMenuStyles: Interpolation = {
  boxShadow: `${dropShadow.none} ${dropShadow.ms} ${dropShadow.m} ${dropShadow.none} ${constantSemanticColors.fixedDarkenVeryLow}`,
};

const dropdownMenuItemErrorStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.errorMedium,
});

const dropdownMenuItemContentStyles: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
};

type RemoveBroadcastDropdownMenuProps = {
  broadcastId: number;
  communityId: number;
};

export const RemoveBroadcastDropdownMenu = ({
  broadcastId,
  communityId,
}: RemoveBroadcastDropdownMenuProps) => {
  const alert = useAlert();
  const { track } = useAnalytics();
  const t = useTranslations();

  const { mutationDelete: deleteBroadcast } = useRequestCommunityBroadcast({
    communityId,
  });

  const dialogDelete = useConfirm({
    cancelButton: t("general_word_cancel"),
    description: () => t("community_delete_confirmation_message"),
    primaryButton: (onClick) => (
      <Button variant="color" color={colors.red60} onClick={onClick}>
        {t("general_word_delete")}
      </Button>
    ),
    title: () => t("dashboard_broadcast_delete_title"),
  });

  const handleDelete = async () => {
    track({
      event: HallowAnalyticsEvent.TappedDeleteBroadcastMessage,
    });
    const confirm = await dialogDelete.show();
    if (!confirm) return;

    try {
      await deleteBroadcast.mutateAsync({ broadcastId });
    } catch (error) {
      alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  return (
    <ModalDropdownMenu
      trigger={<IconButton icon={<MoreIcon />} variant="transparentNeutral" />}
      contentCSS={[dropdownMenuStyles]}
      dropdownMenuProps={{
        align: "end",
        contentProps: { collisionPadding: 20, sideOffset: 5 },
      }}
    >
      <ModalDropdownMenuItem
        modalItemProps={{
          onPointerDown: handleDelete,
        }}
        dropdownMenuItemProps={{
          onClick: handleDelete,
        }}
        dropdownMenuItemCSS={[
          dropdownMenuItemStyles,
          dropdownMenuItemErrorStyles,
        ]}
        modalItemCSS={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
      >
        <div css={dropdownMenuItemContentStyles}>
          <TrashIcon />
          {t("dashboard_broadcast_delete")}
        </div>
      </ModalDropdownMenuItem>
    </ModalDropdownMenu>
  );
};

RemoveBroadcastDropdownMenu.displayName = "RemoveBroadcastDropdownMenu";
